import { useEffect } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";

// The "Context" sub-view value in SessionContextDatasetDetail — it renders
// DatasetAIContext, which polls the init-status endpoint for status/phase/progress.
const AI_CONTEXT_VIEW = "ai-context";

/**
 * When the data-prep agent launches a dataset initialization in chat, open the
 * session context panel to that dataset's Context view — the view that already
 * shows initialization status/phase/progress — so the user watches it in place.
 *
 * Mirrors `useMcpAppsPaneSync`, which opens the panel on a live `mcp_app_ui`
 * event. Navigation only: it adds no poll of its own; the opened Context view's
 * existing `useInitializationStatus` supplies the live status. The `dataset_id`
 * comes from the tool-call arguments.
 */
export function useInitializationPanelSync(agent: AbstractAgent) {
  const { openPanel, setView } = useSessionContextPanel();
  useEffect(() => {
    const sub = agent.subscribe({
      onToolCallEndEvent: ({ toolCallName, toolCallArgs }) => {
        if (toolCallName !== "initialize_dataset") return;
        const datasetId = toolCallArgs.dataset_id;
        if (typeof datasetId !== "string") return;
        openPanel(SessionContextPanelType.DATASET, datasetId);
        setView(AI_CONTEXT_VIEW);
      },
    });
    return () => sub.unsubscribe();
  }, [agent, openPanel, setView]);
}
